import { RefreshCw, CheckCircle, Download, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { APP_VERSION, BUILD_DATE } from "@/version";
import { useSWUpdate } from "@/hooks/useSWUpdate";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { toast } from "sonner";

export function AppVersionCard() {
  const { needRefresh, isChecking, checkForUpdates, updateSW } = useSWUpdate();
  const { isInstalled, canPromptInstall, promptInstall, isIOSDevice } = usePWAInstall();

  // Check if we're on mobile browser (not installed PWA)
  const isMobileBrowser = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && !isInstalled;

  const handleCheckUpdates = async () => {
    const toastId = toast.loading("Vérification...");
    
    try {
      const hasUpdate = await checkForUpdates();
      toast.dismiss(toastId);
      
      if (hasUpdate) {
        toast.success("Nouvelle version disponible !");
      } else {
        toast.success("Vous avez la dernière version");
      }
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Erreur lors de la vérification");
    }
  };

  const handleUpdate = async () => {
    const toastId = toast.loading("Mise à jour en cours...");
    
    try {
      // Marquer qu'une mise à jour est en cours AVANT d'appeler updateSW
      sessionStorage.setItem('sw_updating', Date.now().toString());
      
      const success = await updateSW();
      
      if (success) {
        toast.dismiss(toastId);
        toast.success("Mise à jour appliquée, rechargement...");
        
        // Écouter le message SW_UPDATED pour un reload plus rapide
        const handleSWMessage = (event: MessageEvent) => {
          if (event.data?.type === 'SW_UPDATED') {
            console.log('[AppVersionCard] SW updated, reloading now');
            navigator.serviceWorker.removeEventListener('message', handleSWMessage);
            sessionStorage.removeItem('sw_updating');
            sessionStorage.setItem('sw_last_update', Date.now().toString());
            window.location.reload();
          }
        };
        navigator.serviceWorker.addEventListener('message', handleSWMessage);
        
        // Fallback de sécurité : forcer le reload après 3 secondes
        setTimeout(() => {
          const stillUpdating = sessionStorage.getItem('sw_updating');
          if (stillUpdating) {
            console.log('[AppVersionCard] Fallback reload triggered after 3s');
            sessionStorage.removeItem('sw_updating');
            sessionStorage.setItem('sw_last_update', Date.now().toString());
            window.location.reload();
          }
        }, 3000);
      } else {
        toast.dismiss(toastId);
        toast.info("Aucune mise à jour en attente");
        sessionStorage.removeItem('sw_updating');
      }
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Erreur lors de la mise à jour");
      sessionStorage.removeItem('sw_updating');
    }
  };

  const handleInstall = async () => {
    if (isIOSDevice) {
      toast.info("Appuyez sur le bouton Partager puis 'Sur l'écran d'accueil'");
      return;
    }
    
    const success = await promptInstall();
    if (success) {
      toast.success("Application installée !");
    } else {
      toast.info("Installation annulée");
    }
  };

  // On mobile browser, show install button instead of update
  const renderActionButton = () => {
    if (isMobileBrowser) {
      return (
        <Button
          size="sm"
          variant="gold"
          onClick={handleInstall}
          className="gap-2"
        >
          <Smartphone size={14} />
          Installer l'app
        </Button>
      );
    }

    if (needRefresh && isInstalled) {
      return (
        <Button
          size="sm"
          variant="gold"
          onClick={handleUpdate}
          className="gap-2"
        >
          <Download size={14} />
          Mettre à jour
        </Button>
      );
    }

    // Only show check button if installed PWA
    if (isInstalled) {
      return (
        <Button
          size="sm"
          variant="outline"
          onClick={handleCheckUpdates}
          disabled={isChecking}
          className="gap-2"
        >
          <RefreshCw size={14} className={isChecking ? "animate-spin" : ""} />
          {isChecking ? "Vérification..." : "Vérifier"}
        </Button>
      );
    }

    // Desktop browser - just show version info
    return null;
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-foreground">Version {APP_VERSION}</p>
          <p className="text-xs text-muted-foreground">Build: {BUILD_DATE}</p>
        </div>
        
        {renderActionButton()}
      </div>

      {needRefresh && isInstalled && (
        <div className="flex items-center gap-2 text-sm text-primary">
          <CheckCircle size={14} />
          <span>Une nouvelle version est disponible</span>
        </div>
      )}

      {isMobileBrowser && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Smartphone size={14} />
          <span>Installez l'app pour une meilleure expérience</span>
        </div>
      )}
    </div>
  );
}
